import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Tablet, AlertCircle, CheckCircle2, ArrowLeft, Send, Phone,
  Mail, User, Building2, Hash
} from 'lucide-react';
import Logo from '../components/Logo';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

const ServiceRequest = () => {
  const [formData, setFormData] = useState({
    institutionName: '',
    contactPerson: '',
    email: '',
    phone: '',
    serialNumber: '',
    issueCategory: '',
    issueDescription: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [ticketNumber, setTicketNumber] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const issueCategories = [
    'Hardware Issue (Screen, Battery, etc.)',
    'Software Issue (App crashes, freezing)',
    'Network/Connectivity Problems',
    'Charging Issues',
    'Performance Issues (Slow, lagging)',
    'Audio/Speaker Problems',
    'Camera Issues',
    'Touch Screen Not Responsive',
    'Other'
  ];

  const generateTicketNumber = () => {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 1000);
    return `TAB-${timestamp}-${random}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const ticket = generateTicketNumber();
      setTicketNumber(ticket);

      await addDoc(collection(db, 'serviceRequests'), {
        ticketNumber: ticket,
        institutionName: formData.institutionName,
        contactPerson: formData.contactPerson,
        email: formData.email,
        phone: formData.phone,
        serialNumber: formData.serialNumber,
        issueCategory: formData.issueCategory,
        issueDescription: formData.issueDescription,
        status: 'pending',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      setSubmitted(true);

      const mailtoLink = `mailto:officecalloc@gmail.com?subject=Tablet Service Request - ${ticket}&body=Ticket Number: ${ticket}%0D%0AInstitution: ${formData.institutionName}%0D%0AContact Person: ${formData.contactPerson}%0D%0AEmail: ${formData.email}%0D%0APhone: ${formData.phone}%0D%0ASerial Number: ${formData.serialNumber}%0D%0AIssue Category: ${formData.issueCategory}%0D%0A%0D%0AIssue Description:%0D%0A${formData.issueDescription}`;

      setTimeout(() => {
        window.location.href = mailtoLink;
      }, 2000);
    } catch (error) {
      console.error('Error submitting service request:', error);
      alert('There was an error submitting your request. Please try again or contact us directly.');
      setIsSubmitting(false);
    }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
  };

  const inputCls = 'w-full px-4 py-3 bg-[#080F1E] border border-white/15 rounded-xl text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent';
  const labelCls = 'block text-sm font-semibold text-slate-300 mb-2';

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#060D1F] flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="max-w-2xl w-full bg-[#0C1628] border border-white/10 rounded-3xl p-8 md:p-12 text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring' }}
            className="w-24 h-24 bg-green-500/15 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <CheckCircle2 className="w-12 h-12 text-green-400" />
          </motion.div>

          <h2 className="text-4xl font-bold mb-4 text-white">Request Submitted!</h2>

          <div className="bg-orange-500/10 border border-orange-500/20 p-6 rounded-2xl mb-6">
            <p className="text-sm text-slate-400 mb-2">Your Ticket Number</p>
            <p className="text-3xl font-bold text-orange-400">{ticketNumber}</p>
          </div>

          <div className="space-y-4 text-left bg-[#080F1E] border border-white/8 p-6 rounded-2xl mb-8">
            {[
              'Your service request has been recorded and submitted to our support team.',
              `You will receive a confirmation email shortly at ${formData.email}.`,
              'Our technical team will contact you within 24–48 hours.',
              'Please keep your ticket number for future reference.',
            ].map((msg, i) => (
              <div key={i} className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <p className="text-slate-300 text-sm">{msg}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/"
              className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 rounded-xl font-semibold transition-colors inline-flex items-center justify-center gap-2">
              Back to Home
            </Link>
            <button
              onClick={() => {
                setSubmitted(false);
                setFormData({ institutionName: '', contactPerson: '', email: '', phone: '', serialNumber: '', issueCategory: '', issueDescription: '' });
              }}
              className="border border-white/20 text-white px-8 py-3 rounded-xl font-semibold hover:bg-white/8 transition-colors inline-flex items-center justify-center gap-2"
            >
              Submit Another Request
            </button>
          </div>

          <div className="mt-8 pt-8 border-t border-white/10">
            <p className="text-slate-400 mb-4 text-sm">Need immediate assistance?</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:+918971504091"
                className="flex items-center justify-center gap-2 text-orange-400 hover:text-orange-300 font-semibold text-sm">
                <Phone className="w-4 h-4" /> +91 8971504091
              </a>
              <a href="mailto:officecalloc@gmail.com"
                className="flex items-center justify-center gap-2 text-orange-400 hover:text-orange-300 font-semibold text-sm">
                <Mail className="w-4 h-4" /> officecalloc@gmail.com
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#060D1F]">
      {/* Header */}
      <header className="bg-[#060D1F]/95 backdrop-blur-xl border-b border-white/10 sticky top-0 z-40">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <Logo size="md" />
            </Link>
            <Link to="/"
              className="flex items-center gap-2 text-slate-400 hover:text-orange-400 transition-colors font-semibold text-sm">
              <ArrowLeft className="w-5 h-5" /> Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 bg-[#080F1E] relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600/8 rounded-full blur-[100px]" />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-orange-500/8 rounded-full blur-[80px]" />
        <div className="absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: 'radial-gradient(circle,#fff 1px,transparent 1px)', backgroundSize: '36px 36px' }} />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div initial="hidden" animate="visible" variants={fadeInUp} className="text-center max-w-3xl mx-auto">
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ duration: 0.5 }}
              className="inline-block mb-6">
              <div className="w-20 h-20 bg-blue-500/15 border border-blue-500/25 rounded-2xl flex items-center justify-center mx-auto">
                <Tablet className="w-10 h-10 text-blue-400" />
              </div>
            </motion.div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Tablet Service Request</h1>
            <p className="text-xl text-slate-400 mb-6">Submit your tablet issues and our technical team will assist you promptly</p>
            <div className="flex items-center justify-center gap-2 text-slate-500">
              <AlertCircle className="w-5 h-5 text-orange-400" />
              <span className="text-sm">Average response time: 24–48 hours</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Form */}
      <section className="py-16 bg-[#060D1F]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}
            className="max-w-4xl mx-auto">
            <div className="bg-[#0C1628] border border-white/10 p-8 md:p-12 rounded-3xl">
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-white mb-2">Submit Service Request</h2>
                <p className="text-slate-400">Please provide details about the tablet issue you're experiencing</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-8">

                {/* Institution Details */}
                <div>
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-blue-400" /> Institution Details
                  </h3>
                  <div>
                    <label className={labelCls}>Institution / College Name *</label>
                    <select required value={formData.institutionName}
                      onChange={(e) => setFormData({ ...formData, institutionName: e.target.value })}
                      className={inputCls}>
                      <option value="">Select your institution</option>
                      <option value="Acharya PU College">Acharya PU College</option>
                      <option value="IMSC PU College">IMSC PU College</option>
                    </select>
                  </div>
                </div>

                {/* Contact Details */}
                <div>
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <User className="w-5 h-5 text-blue-400" /> Contact Information
                  </h3>
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className={labelCls}>Contact Person *</label>
                      <input type="text" required value={formData.contactPerson}
                        onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
                        placeholder="Your name" className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>Email *</label>
                      <input type="email" required value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="your@email.com" className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>Phone Number *</label>
                      <input type="tel" required value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="+91 XXXXXXXXXX" className={inputCls} />
                    </div>
                  </div>
                </div>

                {/* Tablet Details */}
                <div>
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <Tablet className="w-5 h-5 text-blue-400" /> Tablet Information
                  </h3>
                  <div>
                    <label className={labelCls}>Serial Number (if available)</label>
                    <input type="text" value={formData.serialNumber}
                      onChange={(e) => setFormData({ ...formData, serialNumber: e.target.value })}
                      placeholder="Serial / IMEI number" className={inputCls} />
                  </div>
                </div>

                {/* Issue Details */}
                <div>
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-orange-400" /> Issue Details
                  </h3>
                  <div className="space-y-5">
                    <div>
                      <label className={labelCls}>Issue Category *</label>
                      <select required value={formData.issueCategory}
                        onChange={(e) => setFormData({ ...formData, issueCategory: e.target.value })}
                        className={inputCls}>
                        <option value="">Select issue category</option>
                        {issueCategories.map((cat, i) => <option key={i} value={cat}>{cat}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>Detailed Issue Description *</label>
                      <textarea required value={formData.issueDescription}
                        onChange={(e) => setFormData({ ...formData, issueDescription: e.target.value })}
                        rows="6" className={`${inputCls} resize-none`}
                        placeholder="Please describe the issue in detail. Include when it started, what you were doing when it occurred, and any error messages you've seen..." />
                    </div>
                  </div>
                </div>

                {/* Submit */}
                <div className="pt-6 border-t border-white/10">
                  <button type="submit" disabled={isSubmitting}
                    className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white py-4 rounded-xl font-bold text-lg transition-colors flex items-center justify-center gap-2">
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Submitting…
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" /> Submit Service Request
                      </>
                    )}
                  </button>
                  <p className="text-sm text-slate-500 text-center mt-4">
                    By submitting this form, you agree to our service terms and conditions
                  </p>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What Happens Next */}
      <section className="py-16 bg-[#080F1E]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-3xl font-bold text-white mb-10">What Happens Next?</h3>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {[
              { icon: Hash,          title: '1. Ticket Generated', desc: "You'll receive a unique ticket number for tracking" },
              { icon: Phone,         title: '2. Team Contact',     desc: 'Our technicians will reach out within 24–48 hours' },
              { icon: CheckCircle2,  title: '3. Issue Resolved',   desc: "We'll fix your tablet with quality service" },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-[#0C1628] border border-white/10 p-6 rounded-2xl">
                <div className="w-16 h-16 bg-blue-500/15 border border-blue-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Icon className="w-8 h-8 text-blue-400" />
                </div>
                <h4 className="font-bold text-white text-lg mb-2">{title}</h4>
                <p className="text-slate-400 text-sm">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#060D1F] border-t border-white/8 py-8">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Logo size="sm" theme="dark" />
            <span className="text-xl font-bold text-white">CALLOC Technologies</span>
          </div>
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} CALLOC Technologies Private Limited. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ServiceRequest;
